import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { DataQueryParamsInterface, SortDir } from "../../model/DataQueryParams";
import ContentType from "./ContentType";
import getAuthToken from "../../core/getAuthToken";

export default interface ApiConnection extends AxiosInstance {
    get<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: ApiConnectionConfig<D>): Promise<R>;
}

export interface ApiConnectionConfig<T = any> extends AxiosRequestConfig<T> {
    extra?: { dataQueryParams?: Partial<DataQueryParamsInterface>, cancelable?: boolean };
}

export const createApiConnection = (params: { baseUrl: string} ): ApiConnection => {
    const connection = axios.create({
        baseURL: params.baseUrl,
        headers: {
            'accept': 'application/json'
        }
    });

    connection.interceptors
        .request
        .use((config: ApiConnectionConfig) => {
            config = addAuthHeader(config);
            config = addDataQueryParams(config);
            config = addResponseType(config);
            config = handleCancelableRequest(config);
            return config;
        });

    connection.interceptors
        .response
        .use(response => {
            const contentDisposition = response.headers?.['content-disposition'];
            if (contentDisposition) {
                const filename = contentDisposition.match(/filename="(.*)"/)?.[1];

                if (filename) {
                    return {
                        ...response,
                        data: new File([response.data], filename)
                    };
                }
            }

            return response;
        })

    return connection
}

const addAuthHeader = (config: AxiosRequestConfig): AxiosRequestConfig => {
    config.headers = {
        ...config.headers,
        authorization: 'Bearer ' + (getAuthToken() || ''),
        'x-authorization': 'Bearer ' + (getAuthToken() || ''), //for test server
    };

    return config;
}

const addDataQueryParams = (config: ApiConnectionConfig) => {
    const pagination = config.extra?.dataQueryParams?.pagination;
    const filters = config.extra?.dataQueryParams?.filters;
    const search = config.extra?.dataQueryParams?.search;

    const params = {};

    if (search) {
        params['s'] = search;
    }

    if (pagination) {
        params['p.page'] = pagination.page;
        params['p.size'] = pagination.size;
    }

    if (!!pagination?.sort?.length) {
        params['p.sort'] = (pagination.sort || [])
            .map(sort => (sort.dir === SortDir.DESC ? '-' : '+') + sort.field)
            .join(',');
    }

    if (filters) {
        Object.keys(filters).map(field => {
            const value = filters[field] ?? undefined;

            if (value === undefined) {
                return;
            }

            if (Array.isArray(value) && value.length > 0) {
                params['f.' + field] = (value as Array<any>).join('|');
            } else if (typeof value === 'object' && Object.keys(value).length > 0) {
                params['f.' + field] = JSON.stringify(value);
            } else if (typeof value === 'boolean') {
                params['f.' + field] = value ? 1 : 0;
            } else {
                params['f.' + field] = value;
            }
        });
    }

    config.params = {
        ...config.params,
        ...params
    };


    return config;
}

const addResponseType = (config: AxiosRequestConfig): AxiosRequestConfig => {
    const blobContentType: Array<string> = [
        ContentType.DOC as string, ContentType.PDF as string, ContentType.XLSX as string
    ];

    if (blobContentType.includes(config.headers?.accept || '')) {
        config.responseType = 'blob';
    }

    return config;
}

const abortControllers: {[url: string]: AbortController} = {};
const handleCancelableRequest = (config: ApiConnectionConfig): AxiosRequestConfig => {
    if (!config.extra?.cancelable || config.headers?.authorization.match('TEST')) {
        return config;
    }

    const url = config.url?.replace(/\?.*/, '');

    if (!url) {
        return config;
    }

    if (abortControllers[url]) {
        abortControllers[url].abort('Request repeated');
    }

    abortControllers[url] = new AbortController();

    config.signal = abortControllers[url].signal;

    return config;
}